
import React, { useEffect, useState } from "react";
import {
  AppBar,
  Avatar,
  Box,
  Button,
  Card,
  CardContent,
  Chip,
  CircularProgress,
  CssBaseline,
  Divider,
  Drawer,
  Grid,
  IconButton,
  List,
  ListItemButton,
  ListItemIcon,
  ListItemText,
  Toolbar,
  Typography,
  useMediaQuery,
} from "@mui/material";
import { useTheme } from "@mui/material/styles";

import DashboardOutlinedIcon from "@mui/icons-material/DashboardOutlined";
import PersonOutlineOutlinedIcon from "@mui/icons-material/PersonOutlineOutlined";
import FactCheckOutlinedIcon from "@mui/icons-material/FactCheckOutlined";
import MenuBookOutlinedIcon from "@mui/icons-material/MenuBookOutlined";
import ApartmentOutlinedIcon from "@mui/icons-material/ApartmentOutlined";
import EventNoteOutlinedIcon from "@mui/icons-material/EventNoteOutlined";
import MenuIcon from "@mui/icons-material/Menu";
import LogoutOutlinedIcon from "@mui/icons-material/LogoutOutlined";
import SchoolOutlinedIcon from "@mui/icons-material/SchoolOutlined";
import CloseOutlinedIcon from "@mui/icons-material/CloseOutlined";

import { useNavigate } from "react-router-dom";
import Footer from "../../../components/Homepage/Footer/Footer";
import Navbar from "../../../components/Homepage/Navbar/Navbar";

const API_URL = "http://localhost:5004";
const DRAWER_WIDTH = 260;

const menuItems = [
  {
    label: "Dashboard",
    icon: <DashboardOutlinedIcon />,
  },
  {
    label: "My Profile",
    icon: <PersonOutlineOutlinedIcon />,
  },
  {
    label: "Attendance",
    icon: <FactCheckOutlinedIcon />,
  },
  {
    label: "Examinations & Results",
    icon: <SchoolOutlinedIcon />,
  },
  {
    label: "Library",
    icon: <MenuBookOutlinedIcon />,
  },
  {
    label: "Hostel",
    icon: <ApartmentOutlinedIcon />,
  },
  {
    label: "Academic Services",
    icon: <EventNoteOutlinedIcon />,
  },
];

function DetailItem({ label, value }) {
  return (
    <Box sx={{ py: 1.5, minWidth: 0 }}>
      <Typography
        variant="caption"
        color="text.secondary"
        sx={{ display: "block", mb: 0.5 }}
      >
        {label}
      </Typography>

      <Typography
        variant="body2"
        fontWeight={600}
        sx={{ overflowWrap: "anywhere" }}
      >
        {value || "Not available"}
      </Typography>
    </Box>
  );
}

function SummaryCard({ title, value, subtitle, icon, color }) {
  return (
    <Card
      elevation={0}
      sx={{
        height: "100%",
        border: "1px solid #e5eaf2",
        borderRadius: 3,
      }}
    >
      <CardContent sx={{ p: 2.5 }}>
        <Box
          sx={{
            display: "flex",
            justifyContent: "space-between",
            alignItems: "flex-start",
            gap: 1,
          }}
        >
          <Box>
            <Typography color="text.secondary" variant="body2">
              {title}
            </Typography>

            <Typography
              variant="h5"
              fontWeight={800}
              sx={{ mt: 1, overflowWrap: "anywhere" }}
            >
              {value || "—"}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: "block", mt: 1 }}
            >
              {subtitle}
            </Typography>
          </Box>

          <Avatar
            variant="rounded"
            sx={{
              bgcolor: color,
              color: "#fff",
              width: 46,
              height: 46,
              borderRadius: 2,
            }}
          >
            {icon}
          </Avatar>
        </Box>
      </CardContent>
    </Card>
  );
}

export default function StudentDashboard() {
  const theme = useTheme();
  const isMobile = useMediaQuery(theme.breakpoints.down("md"));
  const navigate = useNavigate();

  const [student, setStudent] = useState(null);
  const [photoUrl, setPhotoUrl] = useState("");
  const [activeTab, setActiveTab] = useState("Dashboard");
  const [mobileMenuOpen, setMobileMenuOpen] = useState(false);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");

  const token = sessionStorage.getItem("erpToken");

  useEffect(() => {
    let cancelled = false;
    let createdPhotoUrl = "";

    async function loadStudentData() {
      if (!token) {
        navigate("/student-erp/login", { replace: true });
        return;
      }

      setLoading(true);
      setError("");

      try {
        const response = await fetch(`${API_URL}/api/student/me`, {
          method: "GET",
          headers: {
            Authorization: `Bearer ${token}`,
          },
        });

        const result = await response.json().catch(() => ({}));

        if (response.status === 401 || response.status === 403) {
          sessionStorage.removeItem("erpToken");
          sessionStorage.removeItem("erpUser");
          navigate("/student-erp/login", { replace: true });
          return;
        }

        if (!response.ok) {
          throw new Error(
            result.message || "Unable to load student profile."
          );
        }

        const studentData = result.student || result.data || result;

        if (!cancelled) {
          setStudent(studentData);
        }

        // Load the profile photo from the protected photo endpoint.
        try {
          const photoResponse = await fetch(
            `${API_URL}/api/student/me/photo`,
            {
              headers: {
                Authorization: `Bearer ${token}`,
              },
            }
          );

          if (photoResponse.ok) {
            const contentType =
              photoResponse.headers.get("content-type") || "";

            if (contentType.startsWith("image/")) {
              const imageBlob = await photoResponse.blob();
              createdPhotoUrl = URL.createObjectURL(imageBlob);

              if (!cancelled) {
                setPhotoUrl(createdPhotoUrl);
              }
            }
          }
        } catch (photoError) {
          // The dashboard remains usable if no photo is available.
          console.warn("Could not load student photo:", photoError);
        }
      } catch (err) {
        if (!cancelled) {
          setError(err.message || "Something went wrong.");
        }
      } finally {
        if (!cancelled) {
          setLoading(false);
        }
      }
    }

    loadStudentData();

    return () => {
      cancelled = true;

      if (createdPhotoUrl) {
        URL.revokeObjectURL(createdPhotoUrl);
      }
    };
  }, [token, navigate]);

  const handleLogout = () => {
    sessionStorage.removeItem("erpToken");
    sessionStorage.removeItem("erpUser");
    navigate("/student-erp/login", { replace: true });
  };

  const handleMenuClick = (label) => {
    setActiveTab(label);
    setMobileMenuOpen(false);
  };

  const fullName = student?.full_name || "Student";
  const initials = fullName
    .trim()
    .split(/\s+/)
    .slice(0, 2)
    .map((part) => part[0])
    .join("")
    .toUpperCase();

  const drawerContent = (
    <Box sx={{ height: "100%", display: "flex", flexDirection: "column" }}>
      <Box
        sx={{
          p: 2.5,
          bgcolor: "#102b50",
          color: "#fff",
          minHeight: 145,
        }}
      >
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            justifyContent: "space-between",
          }}
        >
          <Box sx={{ display: "flex", alignItems: "center", gap: 1.5 }}>
            <Avatar
              variant="rounded"
              sx={{ bgcolor: "#fff", color: "#15375e", width: 43, height: 43 }}
            >
              <SchoolOutlinedIcon />
            </Avatar>

            <Box>
              <Typography fontWeight={800} variant="subtitle1">
                Student ERP
              </Typography>
              <Typography variant="caption" sx={{ color: "#d5e3f5" }}>
                Rayalaseema University
              </Typography>
            </Box>
          </Box>

          {isMobile && (
            <IconButton
              onClick={() => setMobileMenuOpen(false)}
              aria-label="Close navigation"
              sx={{ color: "#fff" }}
            >
              <CloseOutlinedIcon />
            </IconButton>
          )}
        </Box>
      </Box>

      <Box sx={{ p: 2, display: "flex", alignItems: "center", gap: 1.5 }}>
        <Avatar
          src={photoUrl || undefined}
          alt={fullName}
          sx={{
            width: 48,
            height: 48,
            bgcolor: "#dbeafe",
            color: "#15375e",
            fontWeight: 700,
          }}
        >
          {!photoUrl && initials}
        </Avatar>

        <Box sx={{ minWidth: 0 }}>
          <Typography fontWeight={700} noWrap>
            {fullName}
          </Typography>

          <Typography
            variant="caption"
            color="text.secondary"
            sx={{ display: "block" }}
          >
            {student?.hall_ticket || student?.admission_number || "Student"}
          </Typography>

          <Chip
            label={student?.status || "STUDENT"}
            size="small"
            color="success"
            sx={{ mt: 0.5, height: 21, fontSize: 10 }}
          />
        </Box>
      </Box>

      <Divider />

      <Typography
        variant="overline"
        sx={{ px: 2.5, pt: 2, color: "text.secondary" }}
      >
        MAIN MENU
      </Typography>

      <List sx={{ px: 1.5, pt: 1 }}>
        {menuItems.map((item) => {
          const selected = activeTab === item.label;

          return (
            <ListItemButton
              key={item.label}
              selected={selected}
              onClick={() => handleMenuClick(item.label)}
              sx={{
                borderRadius: 2,
                mb: 0.5,
                py: 1.2,
                "&.Mui-selected": {
                  bgcolor: "#e8f0fc",
                  color: "#174780",
                },
                "&.Mui-selected:hover": {
                  bgcolor: "#dce9fb",
                },
                "&.Mui-selected .MuiListItemIcon-root": {
                  color: "#174780",
                },
              }}
            >
              <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
                {item.icon}
              </ListItemIcon>

              <ListItemText
                primary={item.label}
                primaryTypographyProps={{
                  fontSize: 13.5,
                  fontWeight: selected ? 700 : 500,
                }}
              />
            </ListItemButton>
          );
        })}
      </List>

      <Box sx={{ mt: "auto", p: 2 }}>
        <Divider sx={{ mb: 1.5 }} />

        <ListItemButton
          onClick={handleLogout}
          sx={{
            borderRadius: 2,
            color: "#c62828",
            py: 1.2,
          }}
        >
          <ListItemIcon sx={{ minWidth: 40, color: "inherit" }}>
            <LogoutOutlinedIcon />
          </ListItemIcon>
          <ListItemText
            primary="Logout"
            primaryTypographyProps={{ fontWeight: 600, fontSize: 14 }}
          />
        </ListItemButton>

        <Typography
          variant="caption"
          color="text.secondary"
          sx={{ display: "block", textAlign: "center", mt: 2 }}
        >
          © Rayalaseema University
        </Typography>
      </Box>
    </Box>
  );

  const pageTitle =
    activeTab === "Dashboard"
      ? "Student Dashboard"
      : activeTab;

  const renderDashboard = () => (
    <>
      <Box
        sx={{
          background: "linear-gradient(120deg, #15375e 0%, #2364a5 100%)",
          color: "#fff",
          borderRadius: 3,
          p: { xs: 2.5, md: 4 },
          mb: 3,
        }}
      >
        <Typography variant="overline" sx={{ opacity: 0.85 }}>
          RAYALASEEMA UNIVERSITY
        </Typography>

        <Typography
          variant="h4"
          fontWeight={800}
          sx={{ mt: 1, fontSize: { xs: 25, md: 32 } }}
        >
          Welcome, {fullName}!
        </Typography>

        <Typography sx={{ mt: 1, opacity: 0.9 }}>
          Access your student profile and academic services from one place.
        </Typography>

        <Box sx={{ display: "flex", flexWrap: "wrap", gap: 1, mt: 2.5 }}>
          <Chip
            label={`Hall Ticket: ${
              student?.hall_ticket || "Not available"
            }`}
            sx={{ bgcolor: "#ffffff22", color: "#fff" }}
          />

          <Chip
            label={`Status: ${student?.status || "ACTIVE"}`}
            sx={{ bgcolor: "#ffffff22", color: "#fff" }}
          />
        </Box>
      </Box>

      <Typography variant="h6" fontWeight={800} sx={{ mb: 2 }}>
        Academic Overview
      </Typography>

      <Grid container spacing={2.5} sx={{ mb: 4 }}>
        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            title="Admission Number"
            value={student?.admission_number}
            subtitle="Student identification"
            icon={<PersonOutlineOutlinedIcon />}
            color="#2563eb"
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            title="Department"
            value={student?.department}
            subtitle="Registered department"
            icon={<SchoolOutlinedIcon />}
            color="#0f766e"
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            title="Course"
            value={student?.course}
            subtitle="Enrolled programme"
            icon={<MenuBookOutlinedIcon />}
            color="#7c3aed"
          />
        </Grid>

        <Grid item xs={12} sm={6} lg={3}>
          <SummaryCard
            title="Current Semester"
            value={student?.semester}
            subtitle="Academic information"
            icon={<EventNoteOutlinedIcon />}
            color="#c2410c"
          />
        </Grid>
      </Grid>

      <Card
        elevation={0}
        sx={{ border: "1px solid #e5eaf2", borderRadius: 3 }}
      >
        <CardContent sx={{ p: { xs: 2, md: 3 } }}>
          <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
            Quick Services
          </Typography>

          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            Select a service from the sidebar to view its section.
          </Typography>

          <Grid container spacing={2}>
            {menuItems
              .filter((item) => item.label !== "Dashboard")
              .map((item) => (
                <Grid item xs={12} sm={6} md={4} key={item.label}>
                  <Card
                    variant="outlined"
                    onClick={() => handleMenuClick(item.label)}
                    sx={{
                      height: "100%",
                      cursor: "pointer",
                      borderRadius: 2,
                      transition: "0.2s",
                      "&:hover": {
                        borderColor: "#2364a5",
                        bgcolor: "#f6f9ff",
                        transform: "translateY(-2px)",
                      },
                    }}
                  >
                    <CardContent
                      sx={{
                        display: "flex",
                        alignItems: "center",
                        gap: 1.5,
                      }}
                    >
                      <Avatar
                        sx={{
                          bgcolor: "#e8f0fc",
                          color: "#174780",
                        }}
                      >
                        {item.icon}
                      </Avatar>

                      <Typography fontWeight={700} variant="body2">
                        {item.label}
                      </Typography>
                    </CardContent>
                  </Card>
                </Grid>
              ))}
          </Grid>
        </CardContent>
      </Card>
    </>
  );

  const renderProfile = () => (
    <Card
      elevation={0}
      sx={{ border: "1px solid #e5eaf2", borderRadius: 3 }}
    >
      <CardContent sx={{ p: { xs: 2, md: 4 } }}>
        <Box
          sx={{
            display: "flex",
            alignItems: "center",
            gap: 2.5,
            mb: 3,
            flexWrap: "wrap",
          }}
        >
          <Avatar
            src={photoUrl || undefined}
            alt={fullName}
            sx={{
              width: 100,
              height: 100,
              bgcolor: "#dbeafe",
              color: "#15375e",
              fontSize: 30,
              fontWeight: 700,
            }}
          >
            {!photoUrl && initials}
          </Avatar>

          <Box>
            <Typography variant="h5" fontWeight={800}>
              {fullName}
            </Typography>

            <Typography color="text.secondary">
              {student?.course || "Course not available"}
            </Typography>

            <Typography color="text.secondary" variant="body2">
              {student?.department || "Department not available"}
            </Typography>
          </Box>
        </Box>

        <Divider sx={{ mb: 2 }} />

        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          Personal Information
        </Typography>

        <Grid container columnSpacing={4}>
          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Full Name" value={student?.full_name} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Admission Number" value={student?.admission_number} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Hall Ticket Number" value={student?.hall_ticket} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Gender" value={student?.gender} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Date of Birth" value={student?.date_of_birth} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Father's Name" value={student?.father_name} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Mobile Number" value={student?.mobile} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Father's Mobile" value={student?.father_mobile} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Email" value={student?.email} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="College" value={student?.college} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Course" value={student?.course} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Department" value={student?.department} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Admission Year" value={student?.admission_year} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Semester" value={student?.semester} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Category" value={student?.category} />
          </Grid>

          <Grid item xs={12} sm={6} md={4}>
            <DetailItem label="Scholarship" value={student?.scholarship} />
          </Grid>

          <Grid item xs={12}>
            <DetailItem label="Address" value={student?.address} />
          </Grid>
        </Grid>
      </CardContent>
    </Card>
  );

  const renderPlaceholder = (title, description) => (
    <Card
      elevation={0}
      sx={{ border: "1px solid #e5eaf2", borderRadius: 3 }}
    >
      <CardContent sx={{ p: { xs: 3, md: 5 }, textAlign: "center" }}>
        <Avatar
          sx={{
            width: 70,
            height: 70,
            mx: "auto",
            mb: 2,
            bgcolor: "#e8f0fc",
            color: "#174780",
          }}
        >
          {menuItems.find((item) => item.label === title)?.icon || (
            <SchoolOutlinedIcon />
          )}
        </Avatar>

        <Typography variant="h5" fontWeight={800} sx={{ mb: 1 }}>
          {title}
        </Typography>

        <Typography color="text.secondary" sx={{ maxWidth: 500, mx: "auto" }}>
          {description}
        </Typography>

        <Chip
          label="Module not connected yet"
          sx={{ mt: 2 }}
          color="default"
        />
      </CardContent>
    </Card>
  );

  const renderActiveContent = () => {
    switch (activeTab) {
      case "Dashboard":
        return renderDashboard();

      case "My Profile":
        return renderProfile();

      case "Attendance":
        return renderPlaceholder(
          "Attendance",
          "Your attendance records will appear here when the attendance API is connected."
        );

      case "Examinations & Results":
        return renderPlaceholder(
          "Examinations & Results",
          "Examination schedules, hall tickets and published results will appear here when the examination API is connected."
        );

      case "Library":
        return renderPlaceholder(
          "Library",
          "Library membership, issued books and due dates will appear here when the library API is connected."
        );

      case "Hostel":
        return renderPlaceholder(
          "Hostel",
          "Hostel application and accommodation details will appear here when the hostel API is connected."
        );

      case "Academic Services":
        return renderPlaceholder(
          "Academic Services",
          "Academic service requests and related information will appear here when those modules are connected."
        );

      default:
        return renderDashboard();
    }
  };

  if (loading) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          gap: 2,
          bgcolor: "#f5f7fb",
        }}
      >
        <CircularProgress />
        <Typography color="text.secondary">
          Loading student dashboard...
        </Typography>
      </Box>
    );
  }

  if (error) {
    return (
      <Box
        sx={{
          minHeight: "100vh",
          display: "flex",
          flexDirection: "column",
          alignItems: "center",
          justifyContent: "center",
          p: 3,
          bgcolor: "#f5f7fb",
        }}
      >
        <Typography variant="h6" fontWeight={800} sx={{ mb: 1 }}>
          Unable to load dashboard
        </Typography>

        <Typography color="text.secondary" sx={{ mb: 3, textAlign: "center" }}>
          {error}
        </Typography>

        <Button
          variant="contained"
          onClick={() => window.location.reload()}
        >
          Try Again
        </Button>
      </Box>
    );
  }

  return (
    <>
  
    <Box sx={{ display: "flex", minHeight: "100vh", bgcolor: "#f5f7fb" }}>
      <CssBaseline />

      <AppBar
        position="fixed"
        elevation={0}
        sx={{
          zIndex: (theme) => theme.zIndex.drawer + 1,
          bgcolor: "#fff",
          color: "#183153",
          borderBottom: "1px solid #e5eaf2",
        }}
      >
        <Toolbar sx={{ minHeight: "70px !important" }}>
          {isMobile && (
            <IconButton
              edge="start"
              onClick={() => setMobileMenuOpen(true)}
              aria-label="Open navigation"
              sx={{ mr: 1.5, color: "#183153" }}
            >
              <MenuIcon />
            </IconButton>
          )}

          <Box sx={{ flexGrow: 1, minWidth: 0 }}>
            <Typography
              variant="subtitle1"
              fontWeight={800}
              noWrap
              sx={{ fontSize: { xs: 14, sm: 17 } }}
            >
              {pageTitle}
            </Typography>

            <Typography
              variant="caption"
              color="text.secondary"
              sx={{ display: { xs: "none", sm: "block" } }}
            >
              Rayalaseema University · Student Portal
            </Typography>
          </Box>

          <Avatar
            src={photoUrl || undefined}
            alt={fullName}
            sx={{
              width: 39,
              height: 39,
              bgcolor: "#dbeafe",
              color: "#15375e",
              fontWeight: 700,
            }}
          >
            {!photoUrl && initials}
          </Avatar>
        </Toolbar>
      </AppBar>

      <Box
        component="nav"
        sx={{ width: { md: DRAWER_WIDTH }, flexShrink: { md: 0 } }}
      >
        <Drawer
          variant="temporary"
          open={mobileMenuOpen}
          onClose={() => setMobileMenuOpen(false)}
          ModalProps={{ keepMounted: true }}
          sx={{
            display: { xs: "block", md: "none" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
            },
          }}
        >
          {drawerContent}
        </Drawer>

        <Drawer
          variant="permanent"
          open
          sx={{
            display: { xs: "none", md: "block" },
            "& .MuiDrawer-paper": {
              width: DRAWER_WIDTH,
              boxSizing: "border-box",
              borderRight: "1px solid #e5eaf2",
              bgcolor: "#fff",
            },
          }}
        >
          {drawerContent}
        </Drawer>
      </Box>

      <Box
        component="main"
        sx={{
          flexGrow: 1,
          minWidth: 0,
          p: { xs: 2, sm: 3, md: 4 },
          pt: { xs: "90px", md: "102px" },
        }}
      >
        <Box sx={{ maxWidth: 1500, mx: "auto" }}>
          {renderActiveContent()}
        </Box>
      </Box>
    </Box>
    
    </>
  );
}
